import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProjectionStage } from '../components/ProjectionStage';
import { RecordingLoader } from '../components/RecordingLoader';
import { formatTimecode } from '../../shared/format';
import {
  PLAYBACK_SPEEDS,
  PREROLL_MS,
  idleGaps,
  roundElapsed,
  seekMarker,
  stateAt,
  virtualClockOffset,
  type PlaybackRecording,
  type PlaybackSpeed,
} from '../../shared/playback';
import { type LoadedRecording } from '../lib/loadRecording';
import { usePlayer } from '../lib/usePlayer';
import { useGameSounds } from '../lib/useGameSounds';

/**
 * Playback (2026-09-09) — the fourth mode next to moderation, writing and
 * projection: an evening that already happened, played again in real time.
 *
 * A round's export is a complete recording of the game (decision E3), so the
 * replay does not need a re-enactment: it feeds the reconstructed state into
 * the same ProjectionStage the beamer runs live. Same header, same two
 * clocks, same per-author tints, same margin annotations, same red ENDE
 * screen — only the source of the state differs, and only the transport bar
 * at the bottom gives it away. Drop the bar (or press F for full screen) and
 * the audience sees the show it saw the first time.
 *
 * It is a local mode on purpose: no PIN, no session, nothing on the server.
 * The moderation opens `#/play` on the machine that drives the projection,
 * picks the zip it downloaded after the round, and presses play.
 */
export function PlaybackView() {
  const [loaded, setLoaded] = useState<LoadedRecording | null>(null);

  useEffect(() => {
    const url = loaded?.comicUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [loaded]);

  if (!loaded)
    return (
      <RecordingLoader
        title="Playback"
        lead={
          <>
            Eine gespielte Runde noch einmal — in Echtzeit,
            <br />
            auf derselben Leinwand.
          </>
        }
        onLoaded={setLoaded}
      />
    );
  return <PlaybackStage loaded={loaded} onEject={() => setLoaded(null)} />;
}

/* -------------------------------------------------------------- stage --- */

/** The bar fades out while the replay runs and comes back on a mouse move. */
const CHROME_IDLE_MS = 3_500;

function PlaybackStage({ loaded, onEject }: { loaded: LoadedRecording; onEject: () => void }) {
  const rec = loaded.recording;
  const source = useMemo(() => ({ totalMs: rec.totalMs, gaps: idleGaps(rec) }), [rec]);
  const player = usePlayer(source);
  const state = useMemo(() => stateAt(rec, player.playheadMs), [rec, player.playheadMs]);
  const sound = useGameSounds(state, 'projection');
  const [chromeVisible, setChromeVisible] = useState(true);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const now = Date.now();
  const clockOffset = virtualClockOffset(rec, player.playheadMs, now);

  // Auto-hiding chrome: on the beamer the bar must not sit in the picture —
  // but only once the replay is actually running. While it is stopped the
  // controls stay put, because a bar that vanishes before the first click is
  // a bar the moderation has to hunt for. A twitch of the mouse (or the
  // 6-pixel edge the bar leaves behind) brings it back.
  const wake = useCallback(() => {
    setChromeVisible(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setChromeVisible(false), CHROME_IDLE_MS);
  }, []);

  useEffect(() => {
    if (!player.playing) {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      setChromeVisible(true);
      return;
    }
    wake();
    window.addEventListener('mousemove', wake);
    window.addEventListener('touchstart', wake);
    window.addEventListener('keydown', wake);
    return () => {
      window.removeEventListener('mousemove', wake);
      window.removeEventListener('touchstart', wake);
      window.removeEventListener('keydown', wake);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [player.playing, wake]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      switch (e.key) {
        case ' ':
          e.preventDefault();
          player.toggle();
          break;
        case 'ArrowRight':
          e.preventDefault();
          player.nudge(e.shiftKey ? 60_000 : 10_000);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          player.nudge(e.shiftKey ? -60_000 : -10_000);
          break;
        case 'ArrowUp':
          e.preventDefault();
          player.setSpeed(stepSpeed(player.speed, 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          player.setSpeed(stepSpeed(player.speed, -1));
          break;
        case 'Home':
          player.seek(0);
          break;
        case 'End':
          player.seek(rec.totalMs);
          break;
        case '.':
          player.seek(seekMarker(rec, player.playheadMs, 1));
          break;
        case ',':
          player.seek(seekMarker(rec, player.playheadMs, -1));
          break;
        case 'f':
        case 'F':
          void toggleFullscreen();
          break;
        case 'm':
        case 'M':
          sound.setEnabled(!sound.enabled);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [player, rec, sound]);

  const elapsed = roundElapsed(rec, player.playheadMs);
  const preroll = player.playheadMs < PREROLL_MS;

  return (
    <div className={`play ${chromeVisible ? '' : 'play--bare'}`} data-testid="playback-stage">
      <div className="play__screen">
        <ProjectionStage
          state={state}
          now={now}
          clockOffset={clockOffset}
          remainingMs={(seat) => state.round.remainingMs[seat] ?? 0}
          comicSrc={() => loaded.comicUrl ?? ''}
          onSlide={sound.slide}
          soundUnlock={sound.locked && sound.enabled ? sound.unlock : null}
          className="beam--embedded"
        />
      </div>

      <div className="play__bar" onMouseEnter={wake} data-testid="playback-bar">
        <Scrubber rec={rec} playheadMs={player.playheadMs} onSeek={player.seek} />

        <div className="play__controls">
          <div className="play__group">
            <button
              className="btn btn--small"
              onClick={() => player.seek(seekMarker(rec, player.playheadMs, -1))}
              title="Vorheriger Moment (,)"
            >
              ⏮
            </button>
            <button
              className="btn btn--primary play__toggle"
              onClick={player.toggle}
              title="Abspielen / Pause (Leertaste)"
              data-testid="playback-toggle"
            >
              {player.playing ? '❚❚ Pause' : player.finished ? '↻ Nochmal' : '▶ Abspielen'}
            </button>
            <button
              className="btn btn--small"
              onClick={() => player.seek(seekMarker(rec, player.playheadMs, 1))}
              title="Nächster Moment (.)"
            >
              ⏭
            </button>
          </div>

          <div className="play__time" data-testid="playback-time">
            <strong>{preroll ? 'Vorlauf' : formatTimecode(elapsed)}</strong>
            <span>/ {formatTimecode(rec.roundMs)}</span>
          </div>

          <div className="play__group play__group--speed">
            {PLAYBACK_SPEEDS.map((s) => (
              <button
                key={s}
                className={`btn btn--small ${player.speed === s ? 'btn--confirmed' : 'btn--ghost'}`}
                onClick={() => player.setSpeed(s)}
                title={`${s}× Geschwindigkeit`}
                data-testid={`playback-speed-${s}`}
              >
                {s}×
              </button>
            ))}
          </div>

          <div className="play__group">
            <label className="play__check" title="Stellen ohne Anschlag überspringen">
              <input
                type="checkbox"
                checked={player.skipIdle}
                onChange={(e) => player.setSkipIdle(e.target.checked)}
              />
              Leerlauf überspringen
            </label>
            <button
              className="btn btn--small btn--ghost"
              onClick={() => sound.setEnabled(!sound.enabled)}
              title="Ton (M)"
            >
              {sound.enabled ? '🔊' : '🔇'}
            </button>
            <button className="btn btn--small btn--ghost" onClick={() => void toggleFullscreen()} title="Vollbild (F)">
              ⛶
            </button>
          </div>

          <div className="play__meta">
            <strong>
              {rec.kind === 'comic' ? 'Bildrunde' : 'Runde'} {rec.roundNumber}
            </strong>
            <span>
              {rec.authors.map((a) => a.name).join(' × ')} · {rec.wordCount} Wörter · {rec.keystrokeCount} Anschläge
              {rec.hasStoryLayer ? '' : ' · ohne Moderationsspur'}
            </span>
          </div>

          <button className="btn btn--small btn--ghost" onClick={onEject} data-testid="playback-eject">
            Andere Aufnahme
          </button>
        </div>

        {loaded.notes.length > 0 && (
          <ul className="play__notes">
            {loaded.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * The timeline: the recording's own moments as tick marks, so the moderation
 * can jump to the joker or to the turn where it got interesting instead of
 * hunting for it. The pre-roll is drawn as a darker head piece.
 */
function Scrubber({
  rec,
  playheadMs,
  onSeek,
}: {
  rec: PlaybackRecording;
  playheadMs: number;
  onSeek: (ms: number) => void;
}) {
  const pct = (ms: number) => `${(ms / rec.totalMs) * 100}%`;
  return (
    <div className="scrub">
      <div className="scrub__track">
        <div className="scrub__preroll" style={{ width: pct(PREROLL_MS) }} />
        <div className="scrub__done" style={{ width: pct(playheadMs) }} />
        {rec.markers.map((m, i) => (
          <span
            key={`${m.atMs}-${i}`}
            className={`scrub__mark scrub__mark--${m.kind}`}
            style={{ left: pct(m.atMs) }}
            title={`${formatTimecode(Math.max(0, m.atMs - PREROLL_MS))} · ${m.label}`}
          />
        ))}
        <div className="scrub__head" style={{ left: pct(playheadMs) }} />
      </div>
      <input
        className="scrub__input"
        type="range"
        min={0}
        max={rec.totalMs}
        step={100}
        value={playheadMs}
        onChange={(e) => onSeek(Number(e.target.value))}
        aria-label="Position in der Aufnahme"
        data-testid="playback-scrub"
      />
    </div>
  );
}

function stepSpeed(current: PlaybackSpeed, dir: 1 | -1): PlaybackSpeed {
  const i = PLAYBACK_SPEEDS.indexOf(current);
  return PLAYBACK_SPEEDS[Math.min(Math.max(0, i + dir), PLAYBACK_SPEEDS.length - 1)] ?? current;
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    /* the browser may refuse without a gesture — nothing to recover from */
  }
}
